import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { ReactLink } from '../Link/Link'

/**
 * Bronson Breadcrumb component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-breadcrumb.html
 *
 * @param {BreadcrumbItem[]} [children] - A collection of {@link BreadcrumbItem}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Breadcrumb component.
 * @constructor
 */
export function Breadcrumb({ children, testId, ...otherProps }) {
  return (
    <nav {...otherProps} data-testid={testId}>
      <ul className="c-breadcrumb">{children}</ul>
    </nav>
  )
}

Breadcrumb.propTypes = {
  children: PropTypes.node, // Bronson template: 'breadcrumb-item'. Use {@link BreadcrumbItem} component.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

/**
 * Bronson BreadcrumbItem component (nested).
 * @internal
 *
 * @param {React.ReactNode} [children] - The item content.
 * @param {boolean} [current] - Set the current item to active. Also sets the `[aria-current]` attribute on the nested {@link ReactLink}.
 * @param {boolean} [disabled] - Please refer to {@link ReactLink.propTypes.disabled}.
 * @param {string} [href] - Please refer to {@link ReactLink.propTypes.href}.
 * @param {boolean} [normalLink] - Please refer to {@link ReactLink.propTypes.normalLink}.
 * @param {function} [onClick] - Please refer to {@link ReactLink.propTypes.onClick}.
 * @param {string} [state] - Additional CSS classes on the nested {@link ReactLink}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The BreadcrumbItem component.
 * @constructor
 */
export function BreadcrumbItem({
  children,
  current,
  disabled,
  href,
  normalLink,
  onClick,
  state,
  testId,
  ...otherProps
}) {
  const aClassNameList = classNames('c-breadcrumb__link', {
    'is-current': current,
    'is-disabled': disabled,
    [`${state}`]: state,
  }).trim()

  return (
    <li className="c-breadcrumb__item" data-testid={testId}>
      <ReactLink
        className={aClassNameList}
        normalLink={normalLink}
        onClick={onClick}
        href={href}
        aria-current={current ? 'page' : null}
        {...otherProps}
      >
        <span className="c-breadcrumb__label">{children}</span>
      </ReactLink>
    </li>
  )
}

BreadcrumbItem.propTypes = {
  children: PropTypes.node, // Bronson template: 'title'.
  current: PropTypes.bool, // Bronson template: 'current'.
  disabled: PropTypes.bool,
  href: PropTypes.string, // Added for `[href]` attribute.
  normalLink: PropTypes.bool, // Handles the default behavior of an anchor tag.
  onClick: PropTypes.func, // Added for `[onClick]` attribute.
  state: PropTypes.string, // Bronson template: 'state'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

BreadcrumbItem.displayName = 'Breadcrumb.Item'
Breadcrumb.Item = BreadcrumbItem
